import React, { useEffect, useRef } from "react";
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";

// The Upload Service v2 card over the drop area (web and native). Every state
// uses the same lines at the same heights inside a card of fixed size, so
// nothing moves while the numbers change. Fill it from uploadPanelView()
// (presentation.js).

const BRAND_RED = "#F84123";
const TRACK = "#F9DCD6";
const TEXT = "rgb(46,63,82)";
const MUTED = "#6f6f6f";
const ERROR = "#c74733";
const BUTTON = "#3F5B7A";

// Every line has a fixed height; together they make the card's height.
export const CARD_LAYOUT = Object.freeze({
  width: 340,
  paddingHorizontal: 16,
  paddingVertical: 12,
  title: 20,
  filenameGap: 2,
  filename: 16,
  barGapTop: 10,
  bar: 6,
  barGapBottom: 8,
  counts: 18,
  status: 30, // two lines of 15
  actionsGap: 4,
  actions: 30,
});

export const UPLOAD_CARD_WIDTH = CARD_LAYOUT.width;
export const UPLOAD_CARD_HEIGHT =
  CARD_LAYOUT.paddingVertical * 2 +
  CARD_LAYOUT.title +
  CARD_LAYOUT.filenameGap +
  CARD_LAYOUT.filename +
  CARD_LAYOUT.barGapTop +
  CARD_LAYOUT.bar +
  CARD_LAYOUT.barGapBottom +
  CARD_LAYOUT.counts +
  CARD_LAYOUT.status +
  CARD_LAYOUT.actionsGap +
  CARD_LAYOUT.actions;

const ProgressTrack = ({ progress = 0, indeterminate, hidden }) => {
  const value = useRef(new Animated.Value(progress)).current;
  const sweep = useRef(new Animated.Value(0)).current;

  // Glide to each new value instead of jumping.
  useEffect(() => {
    if (indeterminate) return undefined;
    const animation = Animated.timing(value, {
      toValue: progress,
      duration: 450,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [progress, indeterminate, value]);

  useEffect(() => {
    if (!indeterminate) return undefined;
    sweep.setValue(0);
    const loop = Animated.loop(
      Animated.timing(sweep, {
        toValue: 1,
        duration: 1400,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: false,
      })
    );
    loop.start();
    return () => loop.stop();
  }, [indeterminate, sweep]);

  return (
    <View style={[styles.track, hidden && styles.hidden]}>
      {indeterminate ? (
        <Animated.View
          style={[
            styles.fill,
            styles.sweep,
            {
              left: sweep.interpolate({
                inputRange: [0, 1],
                outputRange: ["-35%", "100%"],
              }),
            },
          ]}
        />
      ) : (
        <Animated.View
          style={[
            styles.fill,
            {
              width: value.interpolate({
                inputRange: [0, 1],
                outputRange: ["0%", "100%"],
                extrapolate: "clamp",
              }),
            },
          ]}
        />
      )}
    </View>
  );
};

const UploadProgressCard = ({
  title,
  filename,
  progress,
  indeterminate,
  barHidden,
  percentText,
  sizesText,
  statusText,
  statusTone,
  actions = [],
  onAction,
}) => (
  // Owns its touches: a press on the card isn't a press on the drop area
  // behind it (which opens the file picker).
  <View
    testID="upload-progress-card"
    style={styles.card}
    accessibilityLabel={title}
    onStartShouldSetResponder={() => true}
  >
    <Text style={styles.title} numberOfLines={1}>
      {title || " "}
    </Text>
    <Text style={styles.filename} numberOfLines={1} ellipsizeMode="middle">
      {filename || " "}
    </Text>
    <ProgressTrack
      progress={progress}
      indeterminate={indeterminate}
      hidden={barHidden}
    />
    <View style={styles.countsRow}>
      <Text style={[styles.counts, styles.percent]} numberOfLines={1}>
        {percentText || " "}
      </Text>
      <Text style={[styles.counts, styles.sizes]} numberOfLines={1}>
        {sizesText || " "}
      </Text>
    </View>
    <Text
      style={[styles.status, statusTone === "error" && styles.statusError]}
      numberOfLines={2}
    >
      {statusText || " "}
    </Text>
    <View style={styles.actions}>
      {[0, 1].map((slot) => {
        const action = actions[slot];
        if (!action) return <View key={`slot-${slot}`} style={styles.action} />;
        return (
          <Pressable
            key={action.key}
            accessibilityRole="button"
            onPress={() => onAction?.(action.key)}
            style={({ pressed }) => [
              styles.action,
              pressed && styles.actionPressed,
            ]}
          >
            <Text style={styles.actionText} numberOfLines={1}>
              {action.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  </View>
);

const styles = StyleSheet.create({
  card: {
    width: UPLOAD_CARD_WIDTH,
    maxWidth: "90%",
    height: UPLOAD_CARD_HEIGHT,
    paddingHorizontal: CARD_LAYOUT.paddingHorizontal,
    paddingVertical: CARD_LAYOUT.paddingVertical,
    borderRadius: 10,
    backgroundColor: "#ffffff",
    overflow: "hidden",
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.16,
    shadowRadius: 12,
    elevation: 4,
  },
  title: {
    height: CARD_LAYOUT.title,
    lineHeight: CARD_LAYOUT.title,
    fontSize: 15,
    fontWeight: "700",
    color: TEXT,
  },
  filename: {
    height: CARD_LAYOUT.filename,
    lineHeight: CARD_LAYOUT.filename,
    marginTop: CARD_LAYOUT.filenameGap,
    fontSize: 12,
    color: MUTED,
  },
  track: {
    height: CARD_LAYOUT.bar,
    marginTop: CARD_LAYOUT.barGapTop,
    marginBottom: CARD_LAYOUT.barGapBottom,
    borderRadius: 3,
    backgroundColor: TRACK,
    overflow: "hidden",
  },
  fill: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    borderRadius: 3,
    backgroundColor: BRAND_RED,
  },
  sweep: { width: "35%" },
  hidden: { opacity: 0 },
  countsRow: {
    height: CARD_LAYOUT.counts,
    flexDirection: "row",
    alignItems: "center",
  },
  counts: {
    fontSize: 13,
    lineHeight: CARD_LAYOUT.counts,
    color: TEXT,
    fontVariant: ["tabular-nums"],
  },
  // Right-aligned in its own slot, so 9% and 12% end at the same place.
  percent: { width: 40, textAlign: "right", fontWeight: "600" },
  sizes: { flex: 1, textAlign: "right" },
  status: {
    height: CARD_LAYOUT.status,
    lineHeight: CARD_LAYOUT.status / 2,
    fontSize: 12,
    color: MUTED,
    fontVariant: ["tabular-nums"],
  },
  statusError: { color: ERROR },
  actions: {
    height: CARD_LAYOUT.actions,
    marginTop: CARD_LAYOUT.actionsGap,
    flexDirection: "row",
    justifyContent: "center",
  },
  // Room for the longest label ("View event page") in either slot.
  action: {
    width: 120,
    height: CARD_LAYOUT.actions,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  },
  actionPressed: { backgroundColor: "#eef4fb" },
  actionText: {
    fontSize: 13,
    fontWeight: "600",
    color: BUTTON,
    ...Platform.select({ web: { userSelect: "none" }, default: {} }),
  },
});

export default UploadProgressCard;
